import { ApiResult, BinaryDocument, HttpClient } from "../http.js";
import {
  CreateEconomicSupplierPayload,
  CreatedEconomicSupplierDto,
  CreditorInvoiceBookResultDto,
  EconomicSupplierDraftDto,
  CreditorInvoiceMutationResultDto,
  CreditorInvoiceSupplierCandidatesDto,
  EconomicSupplierCandidateDto,
  CreditorInvoiceWorkflowDto,
  PagedCreditorInvoices,
} from "../types/creditor-invoices.js";

export interface AssignCreditorInvoiceProjectPayload {
  projectId: string;
  lineNumbers?: number[];
  /** Cost type for the allocated lines; omitted = keep (default "materialer"). */
  costTypeKey?: string;
}

export interface ListCreditorInvoicesPagedQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  projectId?: string;
  /** Workflow status as the DTO reports it (`received`, `matched`, …); filtered server-side. */
  status?: string;
}

export class CreditorInvoicesApi {
  constructor(private readonly http: HttpClient) {}

  list(): Promise<ApiResult<CreditorInvoiceWorkflowDto[]>> {
    return this.http.get<CreditorInvoiceWorkflowDto[]>("/api/creditor-invoices");
  }

  /**
   * Server-paged, server-searched sibling of {@link list} / {@link listByProject} in one
   * route. Corresponds to GET /api/creditor-invoices/paged. `search` matches supplier name,
   * invoice number, the document-level case number, or an allocated project's case number
   * or title.
   */
  listPaged(query: ListCreditorInvoicesPagedQuery = {}): Promise<ApiResult<PagedCreditorInvoices>> {
    return this.http.get<PagedCreditorInvoices>("/api/creditor-invoices/paged", { query: { ...query } });
  }

  /** Distinct supplier names across the org's creditor invoices, A→Z. GET /api/creditor-invoices/suppliers. */
  listSuppliers(): Promise<ApiResult<string[]>> {
    return this.http.get<string[]>("/api/creditor-invoices/suppliers");
  }

  get(id: string): Promise<ApiResult<CreditorInvoiceWorkflowDto>> {
    return this.http.get<CreditorInvoiceWorkflowDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}`,
    );
  }

  listByProject(projectId: string): Promise<ApiResult<CreditorInvoiceWorkflowDto[]>> {
    return this.http.get<CreditorInvoiceWorkflowDto[]>(
      `/api/projects/${encodeURIComponent(projectId)}/creditor-invoices`,
    );
  }

  /**
   * The received invoice as a human can read it — the supplier's own PDF when the
   * OIOUBL document embedded one, otherwise Sproom's HTML rendering. Branch on
   * `contentType`; the body is supplier content and must only ever be shown in a
   * sandboxed frame (HTML) or the browser's PDF viewer, never injected into the DOM.
   * 404 `no_preview_available` = manually entered invoice; 502 `preview_unavailable`
   * = Sproom unreachable.
   */
  preview(id: string): Promise<ApiResult<BinaryDocument>> {
    return this.http.getBinaryDocument(
      `/api/creditor-invoices/${encodeURIComponent(id)}/preview`,
      { headers: { accept: "application/pdf, text/html" } },
    );
  }

  assignProject(
    id: string,
    payload: AssignCreditorInvoiceProjectPayload,
  ): Promise<ApiResult<CreditorInvoiceMutationResultDto>> {
    return this.http.put<CreditorInvoiceMutationResultDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/assign-project`,
      { body: payload },
    );
  }

  process(id: string): Promise<ApiResult<CreditorInvoiceMutationResultDto>> {
    return this.http.post<CreditorInvoiceMutationResultDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/process`,
    );
  }

  reject(id: string): Promise<ApiResult<CreditorInvoiceMutationResultDto>> {
    return this.http.post<CreditorInvoiceMutationResultDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/reject`,
    );
  }

  /**
   * Bogfør: books an approved (processed) invoice and posts its supplier voucher to e-conomic.
   * POST /api/creditor-invoices/{id}/book (`creditorInvoices.book`). 409 when the e-conomic
   * supplier must be chosen by hand, e-conomic is not connected/configured, or a booking is in
   * progress; a repeat call on a booked invoice returns its state without posting again.
   */
  book(id: string): Promise<ApiResult<CreditorInvoiceBookResultDto>> {
    return this.http.post<CreditorInvoiceBookResultDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/book`,
    );
  }

  /** The e-conomic supplier(s) Bogfør would use. GET /api/creditor-invoices/{id}/economic-supplier-candidates. */
  economicSupplierCandidates(id: string): Promise<ApiResult<CreditorInvoiceSupplierCandidatesDto>> {
    return this.http.get<CreditorInvoiceSupplierCandidatesDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/economic-supplier-candidates`,
    );
  }

  /** Confirms a supplier number typed by hand. GET /api/creditor-invoices/economic-suppliers/{n} (`integrations.useEconomic`); 404 = does not exist. */
  getEconomicSupplier(supplierNumber: number): Promise<ApiResult<EconomicSupplierCandidateDto>> {
    return this.http.get<EconomicSupplierCandidateDto>(
      `/api/creditor-invoices/economic-suppliers/${encodeURIComponent(String(supplierNumber))}`,
    );
  }

  /** Chooses the e-conomic supplier by hand. PUT /api/creditor-invoices/{id}/link-economic-supplier (`integrations.useEconomic`). */
  linkEconomicSupplier(id: string, economicSupplierNumber: number): Promise<ApiResult<number>> {
    return this.http.put<number>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/link-economic-supplier`,
      { body: { economicSupplierNumber } },
    );
  }

  /** What the "Opret i e-conomic" form starts from. GET /api/creditor-invoices/{id}/economic-supplier-draft (`integrations.economic.use`). */
  economicSupplierDraft(id: string): Promise<ApiResult<EconomicSupplierDraftDto>> {
    return this.http.get<EconomicSupplierDraftDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/economic-supplier-draft`,
    );
  }

  /**
   * Creates the invoice's supplier in e-conomic and links it — or links the one that already has the
   * invoice's CVR. POST /api/creditor-invoices/{id}/economic-supplier (`integrations.economic.use`).
   */
  createEconomicSupplier(
    id: string,
    payload: CreateEconomicSupplierPayload,
  ): Promise<ApiResult<CreatedEconomicSupplierDto>> {
    return this.http.post<CreatedEconomicSupplierDto>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/economic-supplier`,
      { body: payload },
    );
  }

  /** Re-sends the voucher of a booked invoice. POST /api/creditor-invoices/{id}/post-to-economic (`integrations.useEconomic`). */
  postToEconomic(id: string): Promise<ApiResult<number>> {
    return this.http.post<number>(
      `/api/creditor-invoices/${encodeURIComponent(id)}/post-to-economic`,
    );
  }
}
